package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.core.platform.paywallPurchasingButtonText
import org.catharsis.spendtel.feature.data.normalizePriceLabel
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.home.PaywallErrorBanner
import org.catharsis.spendtel.feature.presentation.home.PaywallFeatureList

private val modalShape = RoundedCornerShape(20.dp)
private val modalMaxWidth = 880.dp

@Composable
fun WebReportPaywallModal(
    message: String,
    priceLabel: String,
    isPurchasing: Boolean,
    errorMessage: String? = null,
    onDismiss: () -> Unit,
    onPurchase: () -> Unit,
) {
    val displayPrice = normalizePriceLabel(priceLabel)

    Box(modifier = Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.55f))
                .clickable(
                    enabled = !isPurchasing,
                    onClick = onDismiss,
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() },
                ),
        )

        BoxWithConstraints(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center,
        ) {
            val useSplitLayout = maxWidth >= 720.dp
            val scrollState = rememberScrollState()

            Surface(
                modifier = Modifier
                    .padding(horizontal = 24.dp, vertical = 32.dp)
                    .widthIn(max = modalMaxWidth)
                    .fillMaxWidth(if (maxWidth >= 720.dp) 0.92f else 1f)
                    .heightIn(max = maxHeight * 0.9f),
                shape = modalShape,
                color = Color.White,
                shadowElevation = 16.dp,
                tonalElevation = 0.dp,
            ) {
                if (useSplitLayout) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 480.dp),
                    ) {
                        WebPaywallBrandPanel(
                            message = message,
                            modifier = Modifier
                                .weight(0.95f)
                                .fillMaxHeight(),
                        )
                        WebPaywallCheckoutPanel(
                            displayPrice = displayPrice,
                            isPurchasing = isPurchasing,
                            errorMessage = errorMessage,
                            scrollState = scrollState,
                            onDismiss = onDismiss,
                            onPurchase = onPurchase,
                            modifier = Modifier
                                .weight(1.05f)
                                .fillMaxHeight(),
                        )
                    }
                } else {
                    Column {
                        WebPaywallBrandPanel(
                            message = message,
                            compact = true,
                            modifier = Modifier.fillMaxWidth(),
                        )
                        WebPaywallCheckoutPanel(
                            displayPrice = displayPrice,
                            isPurchasing = isPurchasing,
                            errorMessage = errorMessage,
                            scrollState = scrollState,
                            onDismiss = onDismiss,
                            onPurchase = onPurchase,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun WebPaywallBrandPanel(
    message: String,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    Box(
        modifier = modifier
            .then(
                if (compact) {
                    Modifier.height(200.dp)
                } else {
                    Modifier.fillMaxHeight()
                },
            )
            .background(SpendTelGradients.hero)
            .padding(if (compact) 28.dp else 36.dp),
        contentAlignment = if (compact) Alignment.CenterStart else Alignment.Center,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(if (compact) 12.dp else 16.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(if (compact) 52.dp else 64.dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.16f)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    imageVector = Icons.Outlined.AutoAwesome,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.size(if (compact) 28.dp else 32.dp),
                )
            }

            Text(
                text = "Unlock another\nspending story",
                style = if (compact) {
                    MaterialTheme.typography.headlineSmall
                } else {
                    MaterialTheme.typography.headlineMedium
                },
                fontWeight = FontWeight.Bold,
                color = Color.White,
                lineHeight = if (compact) 28.sp else 34.sp,
            )

            Text(
                text = message,
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.88f),
                lineHeight = 24.sp,
            )
        }
    }
}

@Composable
private fun WebPaywallCheckoutPanel(
    displayPrice: String,
    isPurchasing: Boolean,
    errorMessage: String?,
    scrollState: ScrollState,
    onDismiss: () -> Unit,
    onPurchase: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(modifier = modifier.background(Color.White)) {
        IconButton(
            onClick = onDismiss,
            enabled = !isPurchasing,
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(8.dp),
        ) {
            Icon(
                imageVector = Icons.Filled.Close,
                contentDescription = "Close",
                tint = SpendTelReportColors.inkMuted,
            )
        }

        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 52.dp, bottom = 28.dp)
                .verticalScroll(scrollState),
            contentAlignment = Alignment.Center,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 28.dp),
                verticalArrangement = Arrangement.spacedBy(20.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
            Text(
                text = "What's included",
                modifier = Modifier.fillMaxWidth(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )

            PaywallFeatureList()

            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                color = SpendTelReportColors.canvas,
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 20.dp, vertical = 18.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            text = "One additional report",
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.SemiBold,
                            color = SpendTelReportColors.ink,
                        )
                        Text(
                            text = "Single purchase · no subscription",
                            style = MaterialTheme.typography.bodySmall,
                            color = SpendTelReportColors.inkMuted,
                        )
                    }
                    Text(
                        text = displayPrice,
                        style = MaterialTheme.typography.displaySmall,
                        fontWeight = FontWeight.Bold,
                        color = SpendTelReportColors.violet,
                    )
                }
            }

            errorMessage?.let { PaywallErrorBanner(message = it) }

            Button(
                onClick = onPurchase,
                enabled = !isPurchasing,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(52.dp),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = deepGreen,
                    contentColor = Color.White,
                ),
            ) {
                if (isPurchasing) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp,
                    )
                    Text(
                        text = paywallPurchasingButtonText(),
                        modifier = Modifier.padding(start = 10.dp),
                        fontWeight = FontWeight.Bold,
                    )
                } else {
                    Text(
                        text = "Continue to checkout · $displayPrice",
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                    )
                }
            }

            TextButton(
                onClick = onDismiss,
                enabled = !isPurchasing,
                modifier = Modifier.align(Alignment.CenterHorizontally),
            ) {
                Text(
                    text = "Not now",
                    color = SpendTelReportColors.inkMuted,
                    fontWeight = FontWeight.Medium,
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = Icons.Outlined.Lock,
                    contentDescription = null,
                    tint = SpendTelReportColors.inkMuted,
                    modifier = Modifier.size(14.dp),
                )
                Text(
                    text = "Secure Stripe checkout · Encrypted statements",
                    modifier = Modifier.padding(start = 6.dp),
                    style = MaterialTheme.typography.labelSmall,
                    color = SpendTelReportColors.inkMuted,
                    textAlign = TextAlign.Center,
                )
            }
            }
        }
    }
}
